import { useEffect, useMemo, useRef, useState } from "react"
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from "@/components/ui/drawer"
import { Skeleton } from "@/components/ui/skeleton"
import { Copy, X } from "lucide-react"
import { toast } from "sonner"

import { useRoundDetail } from "./useRoundDetail"
import type {
  PvpRouletteResolvedRound,
  RoundDetailUserData,
} from "@/shared/api/endpoints"
import { useI18n } from "@/shared/i18n"
import { resolveAvatarLoadUrl } from "@/shared/lib/avatarProxy"
import { getAvatarInitials } from "@/shared/lib/avatarInitials"
import { formatHundredthsTrimZeros } from "@/shared/lib/pvpRouletteMath"
import { verifyPvpRoundFairness } from "@/shared/lib/pvpRoundFairnessVerify"
import { buildLegitCheckExternalUrl } from "@/shared/lib/buildLegitCheckExternalUrl"
import { telegram } from "@/shared/lib/telegram"
import {
  type AppLocale,
  localeUsesRuRegionalFormat,
} from "@/shared/i18n/translations"
import {
  formatPvpRoundDisplayDate,
  toMilliseconds,
} from "@/shared/lib/formatPvpRoundDisplayDate"
import { AnalyticsEvent, trackAnalyticsEvent } from "@/shared/lib/analytics"

import TonIcon from "@/assets/icons/ton-icon.svg?react"

import {
  GiftBetThumbnails,
  type GiftThumbnailItem,
} from "@/features/pvp-room/ui/GiftBetThumbnails"

interface Props {
  roundId: string | null
  onClose: () => void
  /** When set (e.g. room emulator preview), skips GET round and renders this payload. */
  previewRound?: PvpRouletteResolvedRound | null
}

function formatParticipantWhen(ms: number, locale: AppLocale) {
  const d = new Date(ms)
  return d.toLocaleString(
    localeUsesRuRegionalFormat(locale) ? "ru-RU" : "en-GB",
    {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }
  )
}

interface ParticipantRow {
  user: RoundDetailUserData
  /** Total TON-equivalent in nanotons (TON + gift `ton_equiv_nano` summed). */
  amountNano: number
  lastAt: number
  pct: number
  gifts: GiftThumbnailItem[]
}

function buildParticipantRows(round: PvpRouletteResolvedRound): ParticipantRow[] {
  const activeBets = round.bets.filter(
    (b) => typeof b.ton_equiv_nano === "number" && b.ton_equiv_nano > 0
  )

  if (activeBets.length > 0) {
    interface Accumulator {
      user: RoundDetailUserData
      tonEquivNano: number
      lastAt: number
      gifts: GiftThumbnailItem[]
    }
    const byUser = new Map<string, Accumulator>()

    for (const b of activeBets) {
      const id = b.user_data.user_id
      const at = toMilliseconds(b.created_at)
      const cur = byUser.get(id) ?? {
        user: b.user_data,
        tonEquivNano: 0,
        lastAt: 0,
        gifts: [],
      }
      cur.tonEquivNano += b.ton_equiv_nano
      cur.lastAt = Math.max(cur.lastAt, at)
      cur.user = b.user_data
      if (b.asset_class === "TELEGRAM_GIFT") {
        cur.gifts.push({
          id: b.id,
          smallImageUrl: b.small_image_url ?? null,
        })
      }
      byUser.set(id, cur)
    }

    const total = [...byUser.values()].reduce((s, v) => s + v.tonEquivNano, 0)
    return [...byUser.values()]
      .map((v) => ({
        user: v.user,
        amountNano: v.tonEquivNano,
        lastAt: v.lastAt,
        pct: total > 0 ? (v.tonEquivNano / total) * 100 : 0,
        gifts: v.gifts,
      }))
      .sort((a, b) => b.amountNano - a.amountNano)
  }

  const flat = round.parties.flatMap((p) => p.player_banks)
  const totalPb = flat.reduce((s, pb) => s + pb.bank, 0)
  const roundAt = toMilliseconds(round.created_at)
  return flat
    .map((pb) => ({
      user: pb.user_data,
      amountNano: pb.bank,
      lastAt: roundAt,
      pct: totalPb > 0 ? (pb.bank / totalPb) * 100 : 0,
      gifts: [],
    }))
    .sort((a, b) => b.amountNano - a.amountNano)
}

const displayName = (u: RoundDetailUserData) =>
  u.username || u.user_id.slice(0, 8)

const ACCENT_32 = "bg-[rgba(126,125,155,0.32)]"

/**
 * Безопасный потолок длины URL для openLink (#d= в хэше).
 * Все вменяемые движки переваривают сотни KB через location.hash, но Telegram
 * in-app WebView на части клиентов начинает шалить выше ~100K. На таких
 * раундах вместо открытия — тост и предложение проверить кнопкой выше.
 */
const LEGIT_CHECK_URL_SAFE_MAX = 100_000

async function openLegitCheckExternalPage(
  round: PvpRouletteResolvedRound,
  t: (path: string, params?: Record<string, string | number>) => string
) {
  trackAnalyticsEvent(AnalyticsEvent.LegitCheckExternalOpen, {
    round_id: round.id,
    round_number: round.number,
  })
  let url: string
  try {
    url = await buildLegitCheckExternalUrl(round)
  } catch {
    toast.error(t("roundDetail.verifyFailed"))
    return
  }
  if (url.length > LEGIT_CHECK_URL_SAFE_MAX) {
    toast.error(t("roundDetail.legitCheckUrlTooLong"))
    return
  }
  if (telegram.isAvailable) {
    telegram.openLink(url, { try_instant_view: false })
  } else {
    window.open(url, "_blank", "noopener,noreferrer")
  }
}

export function RoundDetailSheet({
  roundId,
  onClose,
  previewRound = null,
}: Props) {
  const { t, locale } = useI18n()
  const queryRoundId = previewRound ? null : roundId
  const { data: fetchedRound, isLoading, isError, refetch } =
    useRoundDetail(queryRoundId)
  const round = previewRound ?? fetchedRound
  const [legitVerified, setLegitVerified] = useState(false)
  const [fairnessBusy, setFairnessBusy] = useState(false)
  const [fairnessFailText, setFairnessFailText] = useState<string | null>(
    null
  )

  const sheetOpen = Boolean(roundId ?? previewRound)
  const legitSheetTrackedRef = useRef<string | null>(null)

  useEffect(() => {
    setLegitVerified(false)
    setFairnessFailText(null)
  }, [roundId, previewRound?.id])

  useEffect(() => {
    if (!sheetOpen || !round) {
      if (!sheetOpen) legitSheetTrackedRef.current = null
      return
    }
    if (legitSheetTrackedRef.current === round.id) return
    legitSheetTrackedRef.current = round.id
    trackAnalyticsEvent(AnalyticsEvent.LegitCheckSheetView, {
      round_id: round.id,
      round_number: round.number,
    })
  }, [sheetOpen, round])

  const participants = useMemo(
    () => (round ? buildParticipantRows(round) : []),
    [round]
  )

  const primaryHash = round?.commit_hash ?? round?.client_commit_hash ?? ""
  const salt = round?.salt ?? ""
  const fairnessPreimage =
    typeof round?.fairness_preimage === "string" ? round.fairness_preimage : ""

  const canVerify = Boolean(
    fairnessPreimage.trim() && salt.trim() && (round?.commit_hash ?? "").trim()
  )

  const copy = async (value: string) => {
    if (!value) return
    try {
      await navigator.clipboard.writeText(value)
      toast.success(t("roundDetail.copied"))
    } catch {
      toast.error(t("roundDetail.copyFailed"))
    }
  }

  const showLoading = previewRound ? false : isLoading
  const showError = previewRound ? false : isError

  return (
    <Drawer open={sheetOpen} onOpenChange={(open) => !open && onClose()}>
      <DrawerContent
        showDefaultHandle={false}
        overlayClassName="bg-black/60 backdrop-blur-[5px]"
        className="flex max-h-[90vh] flex-col rounded-t-[24px] border-0 bg-[#24242E] px-0"
      >
        <div className="flex h-4 w-full shrink-0 items-center justify-center pt-2">
          <div className="h-1 w-8 rounded-full bg-white/10" />
        </div>

        <div className="flex min-h-0 flex-1 flex-col">
          <div className="relative flex items-center justify-center px-4 pb-1 pt-1">
            <DrawerTitle className="pt-2 text-center text-2xl font-semibold leading-[29px] tracking-[-0.5px] text-white">
              {t("roundDetail.legitCheckTitle")}
            </DrawerTitle>
            <DrawerClose asChild>
              <button
                type="button"
                className="absolute right-2 top-1 flex size-14 items-center justify-center text-white"
              >
                <X className="size-6" strokeWidth={2} />
              </button>
            </DrawerClose>
          </div>
          <DrawerDescription className="sr-only">
            {t("roundDetail.detailsDescription")}
          </DrawerDescription>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {showLoading ? (
              <LoadingBody />
            ) : showError ? (
              <ErrorState onRetry={() => refetch()} />
            ) : round ? (
              <div className="flex flex-col gap-0 pb-2">
                <div className="flex gap-2 px-4 pb-2 pt-2">
                  <div
                    className={`flex min-h-[72px] flex-1 flex-col items-center justify-center gap-1 rounded-2xl ${ACCENT_32} px-4 py-4 text-center`}
                  >
                    <span className="text-sm font-normal leading-[17px] tracking-[-0.5px] text-white/60">
                      {t("roundDetail.gameLabel")}
                    </span>
                    <span className="text-base font-medium leading-[19px] tracking-[-0.5px] text-white">
                      #{round.number}
                    </span>
                  </div>
                  <div
                    className={`flex min-h-[72px] flex-1 flex-col items-center justify-center gap-1 rounded-2xl ${ACCENT_32} px-4 py-4 text-center`}
                  >
                    <span className="text-sm font-normal leading-[17px] tracking-[-0.5px] text-white/60">
                      {t("roundDetail.dateLabel")}
                    </span>
                    <span className="text-base font-medium leading-[19px] tracking-[-0.5px] text-white">
                      {formatPvpRoundDisplayDate(round.created_at, locale, t)}
                    </span>
                  </div>
                </div>

                <div className="flex flex-col gap-2 px-4 py-2">
                  <button
                    type="button"
                    className={`flex h-[51px] w-full items-center gap-1.5 rounded-2xl ${ACCENT_32} px-4 text-left`}
                    onClick={() => copy(primaryHash)}
                  >
                    <span className="shrink-0 text-base font-medium leading-[19px] tracking-[-0.5px] text-white">
                      {t("roundDetail.hash")}:
                    </span>
                    <span className="min-w-0 flex-1 truncate text-base font-medium leading-[19px] tracking-[-0.5px] text-white/60">
                      {primaryHash ? primaryHash : t("history.na")}
                    </span>
                    <Copy
                      className="size-4 shrink-0 text-white/60"
                      aria-hidden
                    />
                  </button>
                  <button
                    type="button"
                    className={`flex h-[51px] w-full items-center gap-1.5 rounded-2xl ${ACCENT_32} px-4 text-left`}
                    onClick={() => copy(salt)}
                  >
                    <span className="shrink-0 text-base font-medium leading-[19px] tracking-[-0.5px] text-white">
                      {t("roundDetail.saltLabel")}:
                    </span>
                    <span className="min-w-0 flex-1 truncate text-base font-medium leading-[19px] tracking-[-0.5px] text-white/60">
                      {salt ? salt : t("history.na")}
                    </span>
                    <Copy
                      className="size-4 shrink-0 text-white/60"
                      aria-hidden
                    />
                  </button>
                </div>

                <div className="flex flex-col gap-2 px-4 py-2">
                  {participants.map((row) => (
                    <div
                      key={row.user.user_id}
                      className={`flex min-h-16 items-center gap-2 rounded-2xl ${ACCENT_32} px-4 py-3`}
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-2">
                        <div className="size-10 shrink-0 overflow-hidden rounded-full bg-white/10">
                          {row.user.photo_url ? (
                            <img
                              src={resolveAvatarLoadUrl(row.user.photo_url)}
                              alt=""
                              className="size-full object-cover"
                            />
                          ) : (
                            <div className="flex size-full items-center justify-center text-xs font-medium text-white/80">
                              {getAvatarInitials(
                                displayName(row.user),
                                "?",
                                1
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex min-w-0 flex-col gap-1">
                          <span className="truncate text-base font-medium leading-[19px] tracking-[-0.5px] text-white">
                            {displayName(row.user)}
                          </span>
                          <span className="text-xs font-normal leading-4 tracking-[-0.5px] text-white/60">
                            {formatParticipantWhen(row.lastAt, locale)}
                          </span>
                        </div>
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1 text-right">
                        <span className="text-base font-medium leading-[19px] tracking-[-0.5px] text-white">
                          {row.pct.toFixed(2)}%
                        </span>
                        <div className="flex items-end gap-1.5">
                          {row.gifts.length > 0 && (
                            <GiftBetThumbnails
                              gifts={row.gifts}
                              tone="bubble"
                            />
                          )}
                          <span className="flex items-center gap-0.5 text-xs font-normal leading-4 tracking-[-0.5px] text-white/60">
                            {formatHundredthsTrimZeros(row.amountNano / 1e9)}{" "}
                            {t("common.ton")}
                            <TonIcon className="size-3 shrink-0" aria-hidden />
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <ErrorState onRetry={() => refetch()} />
            )}
          </div>

          {round && !showLoading && !showError ? (
            <div className="flex flex-col gap-2 px-4 pb-3 pt-2">
              {legitVerified ? (
                <div
                  className={`flex h-[60px] w-full items-center justify-center rounded-2xl ${ACCENT_32}`}
                >
                  <p className="text-center text-base font-bold leading-[19px] tracking-[-0.5px] text-[#19AF2A]">
                    {t("roundDetail.legitSuccess", { number: round.number })}
                  </p>
                </div>
              ) : (
                <>
                  {fairnessFailText ? (
                    <div
                      className={`rounded-2xl px-4 py-3 text-center text-sm font-normal leading-[18px] tracking-[-0.5px] text-[#FF6B6B] ${ACCENT_32}`}
                    >
                      {fairnessFailText}
                    </div>
                  ) : null}
                  <button
                    type="button"
                    className="flex h-[60px] w-full items-center justify-center rounded-2xl bg-white text-base font-bold leading-[19px] tracking-[-0.5px] text-[#1B1C21] transition-opacity disabled:opacity-40"
                    disabled={!canVerify || fairnessBusy}
                    onClick={async () => {
                      if (!canVerify || !round) {
                        toast.error(t("roundDetail.verifyFailed"))
                        return
                      }
                      setFairnessFailText(null)
                      setFairnessBusy(true)
                      try {
                        const result = await verifyPvpRoundFairness({
                          fairness_preimage: round.fairness_preimage,
                          salt: round.salt,
                          commit_hash: round.commit_hash,
                          client_commit_hash: round.client_commit_hash,
                          roulette_position: round.roulette_position,
                        })
                        if (result.ok === true) {
                          setLegitVerified(true)
                        } else {
                          const msg = t("roundDetail.verifyFailed")
                          setFairnessFailText(msg)
                          toast.error(msg)
                        }
                      } finally {
                        setFairnessBusy(false)
                      }
                    }}
                  >
                    {fairnessBusy
                      ? t("roundDetail.verifying")
                      : t("roundDetail.checkAction")}
                  </button>
                </>
              )}
              {canVerify ? (
                <button
                  type="button"
                  className={`flex h-[60px] w-full items-center justify-center rounded-2xl text-base font-bold leading-[19px] tracking-[-0.5px] text-white/90 transition-opacity active:opacity-90 ${ACCENT_32}`}
                  onClick={() => {
                    if (round) {
                      void openLegitCheckExternalPage(round, t)
                    }
                  }}
                >
                  {t("roundDetail.openLegitInBrowser")}
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      </DrawerContent>
    </Drawer>
  )
}

function LoadingBody() {
  return (
    <div className="space-y-3 px-4 py-4">
      <div className="flex gap-2">
        <Skeleton className="h-[72px] flex-1 rounded-2xl bg-white/10" />
        <Skeleton className="h-[72px] flex-1 rounded-2xl bg-white/10" />
      </div>
      <Skeleton className="h-[51px] w-full rounded-2xl bg-white/10" />
      <Skeleton className="h-[51px] w-full rounded-2xl bg-white/10" />
      <Skeleton className="h-16 w-full rounded-2xl bg-white/10" />
    </div>
  )
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  const { t } = useI18n()
  return (
    <div className="mx-4 my-4 space-y-3 rounded-2xl bg-white/5 p-4 text-center">
      <p className="text-sm text-white/70">{t("roundDetail.loadFailed")}</p>
      <button
        type="button"
        onClick={onRetry}
        className="text-sm font-medium text-white underline-offset-2 hover:underline"
      >
        {t("roundDetail.retry")}
      </button>
    </div>
  )
}
